import { Box } from '@tempmee/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DebouncedSearchField } from '../../commonComponents/DebouncedSearchField';
import { LabelledSelectField } from '../../commonComponents/LabelledSelectField';
import { ListPage } from '../../commonComponents/ListPage';
import { Spinner } from '../../commonComponents/Spinner';
import { Pagination } from '../../commonComponents/Table/Pagination';
import { isValidBookingFeeInput, parseBookingFee } from '../../../utils/BookingFeeUtils';
import { useDsoControls } from './hooks';

const NET_TERMS_OPTIONS = [
  { label: '-', value: null },
  { label: '14 days', value: 14 },
  { label: '21 days', value: 21 },
  { label: '30 days', value: 30 },
];

const PRE_APPROVAL_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const NetTermsDropdown = ({ value, onChange, disabled }) => (
  <select
    aria-label="Net Terms"
    value={value === null || value === undefined ? '' : String(value)}
    onChange={(e) => {
      const val = e.target.value === '' ? null : Number(e.target.value);
      onChange(val);
    }}
    disabled={disabled}
    style={{ width: 140, height: 32, cursor: 'pointer' }}
  >
    {NET_TERMS_OPTIONS.map((option) => (
      <option key={String(option.value)} value={option.value === null ? '' : String(option.value)}>
        {option.label}
      </option>
    ))}
  </select>
);

const formatBookingFeeValue = (rate) => {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return '';
  return String(Number(rate));
};

const BookingFeeCell = ({ value, onSave, disabled }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [validationError, setValidationError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const enterEditMode = () => {
    if (disabled || isEditing) return;
    setDraft(formatBookingFeeValue(value));
    setValidationError('');
    setIsEditing(true);
  };

  const exitEditMode = () => {
    setIsEditing(false);
    setValidationError('');
  };

  const handleSave = () => {
    if (!isValidBookingFeeInput(draft)) {
      setValidationError('Enter a number between 0.00 and 100.00 (up to 2 decimals).');
      return;
    }
    const parsed = parseBookingFee(draft);
    if (parsed === value) {
      exitEditMode();
      return;
    }
    onSave(parsed, {
      onSuccess: () => exitEditMode(),
    });
  };

  const inputValue = isEditing ? draft : formatBookingFeeValue(value);

  const inputStyle = {
    width: 90,
    height: 32,
    padding: '0 10px',
    textAlign: 'right',
    border: isEditing ? '2px solid #495057' : '1px solid #ced4da',
    borderRadius: 4,
    boxShadow: isEditing ? '0 1px 4px rgba(0, 0, 0, 0.12)' : 'none',
    cursor: !isEditing && !disabled ? 'pointer' : 'text',
    backgroundColor: disabled ? '#f1f3f5' : '#fff',
    outline: 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        aria-label="RDH Booking Fee"
        value={inputValue}
        readOnly={!isEditing}
        onClick={!isEditing ? enterEditMode : undefined}
        onFocus={!isEditing ? enterEditMode : undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          if (validationError) setValidationError('');
        }}
        disabled={disabled}
        style={inputStyle}
      />
      <span style={{ fontWeight: 600 }}>%</span>
      {isEditing && (
        <>
          <button
            type="button"
            className="btn btn-success rounded-pill"
            onClick={handleSave}
            disabled={disabled}
            style={{ height: 32, padding: '0 18px', fontWeight: 500 }}
          >
            save
          </button>
          <button
            type="button"
            className="btn btn-light rounded-pill"
            onClick={exitEditMode}
            disabled={disabled}
            style={{ height: 32, padding: '0 18px', fontWeight: 500, border: '1px solid #ced4da' }}
          >
            cancel
          </button>
        </>
      )}
      {validationError && (
        <span role="alert" style={{ color: '#c0392b', fontSize: 12, width: '100%' }}>
          {validationError}
        </span>
      )}
    </div>
  );
};

const CancellationPolicyDropdown = ({ value, options, defaultPolicyId, onChange, disabled }) => {
  const selectedValue = value ?? defaultPolicyId ?? '';
  return (
    <select
      aria-label="Cancellation Policy"
      value={selectedValue}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || options.length === 0}
      style={{ width: 220, height: 32, cursor: 'pointer' }}
    >
      {options.map((policy) => (
        <option key={policy.id} value={policy.id}>
          {policy.title}
        </option>
      ))}
    </select>
  );
};

export const DsoControls = () => {
  const {
    dsos,
    isLoading,
    page,
    setPage,
    setSearch,
    totalPages,
    total,
    updateNetTerms,
    isUpdatingNetTerms,
    updateBookingFeeRate,
    isUpdatingBookingFeeRate,
    updatePreApproval,
    isUpdatingPreApproval,
    cancellationPolicies,
    defaultCancellationPolicyId,
    updateCancellationPolicy,
    isUpdatingCancellationPolicy,
  } = useDsoControls();

  const handleSearchChange = useCallback(
    (value) => {
      setSearch(value);
      setPage(1);
    },
    [setSearch, setPage],
  );

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <ListPage
      title="DSO-Level Controls"
      isLoading={isLoading}
      total={total}
      filters={(
        <DebouncedSearchField
          label="Search DSO by name"
          placeholder="Search DSO by name..."
          delay={400}
          onChange={handleSearchChange}
        />
      )}
    >
      <table style={{ width: '100%' }}>
        <thead>
          <tr
            style={{
              width: '100%',
              borderBottom: '1px solid #efefef',
              padding: '5px 10px',
              backgroundColor: 'lightblue',
            }}
          >
            <td className="card-text table-title-text" style={{ textAlign: 'left', paddingLeft: 32 }}>
              DSO Name
            </td>
            <td className="card-text table-title-text" style={{ textAlign: 'left', width: 180 }}>
              Net Terms
            </td>
            <td className="card-text table-title-text" style={{ textAlign: 'left', width: 360 }}>
              RDH Booking Fee
            </td>
            <td className="table-title-text">
              <Box width="160px" className="text-left">
                Pre Approvals
              </Box>
            </td>
            <td className="card-text table-title-text" style={{ textAlign: 'left', width: 240 }}>
              Cancellation Policy
            </td>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', padding: 20 }}>
                <Spinner />
              </td>
            </tr>
          )}
          {!isLoading && dsos.length === 0 && (
            <tr>
              <td colSpan={5} className="card-text table-value-text">
                No DSOs found
              </td>
            </tr>
          )}
          {!isLoading &&
            dsos.map((dso) => (
              <tr
                key={dso.id}
                style={{
                  width: '100%',
                  borderBottom: '1px solid #efefef',
                  padding: '5px 10px',
                  alignItems: 'center',
                }}
              >
                <td
                  className="card-text table-value-text"
                  style={{ textAlign: 'left', paddingLeft: 32, paddingRight: 16 }}
                >
                  {dso.name}
                </td>
                <td className="card-text table-value-text" style={{ textAlign: 'left' }}>
                  <NetTermsDropdown
                    value={dso.netTerms}
                    onChange={(netTerms) => updateNetTerms(dso.id, netTerms)}
                    disabled={isUpdatingNetTerms}
                  />
                </td>
                <td className="card-text table-value-text" style={{ textAlign: 'left' }}>
                  <BookingFeeCell
                    value={dso.bookingFeeRate}
                    onSave={(rate, options) => updateBookingFeeRate(dso.id, rate, options)}
                    disabled={isUpdatingBookingFeeRate}
                  />
                </td>
                <td className="table-value-text">
                  <Box width="140px" className="text-left">
                    <LabelledSelectField
                      ariaLabel="Pre Approvals"
                      options={PRE_APPROVAL_OPTIONS}
                      value={dso.is_pre_approved === true}
                      onChange={(isPreApproved) => {
                        // The DS select fires onChange when the current option is re-picked, and
                        // every save fans out to (and locks) all of the DSO's offices.
                        if (isPreApproved === (dso.is_pre_approved === true)) return;
                        updatePreApproval(dso.id, isPreApproved);
                      }}
                      disabled={isUpdatingPreApproval}
                    />
                  </Box>
                </td>
                <td className="card-text table-value-text" style={{ textAlign: 'left' }}>
                  <CancellationPolicyDropdown
                    value={dso.cancellationPolicy?.id}
                    options={cancellationPolicies}
                    defaultPolicyId={defaultCancellationPolicyId}
                    onChange={(policyId) => updateCancellationPolicy(dso.id, policyId)}
                    disabled={isUpdatingCancellationPolicy}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
      )}
    </ListPage>
  );
};
